/**
 * One reasoning evaluation run: each check with its value, rule and reason. Template example: NextAdmin
 * `tables/basic-tables/_component/leads-report` (a compact table in a panel), in the shared `Sheet`.
 */
import type React from 'react';
import { Badge, DataTable, type DataTableColumn, Sheet } from '../../../components/ui/index.js';
import type { ReasoningEvaluation, ReasoningEvaluationCheck } from '../../../generated/api/types.gen.js';
import { askedOn, CHECK_STATUS, checkValue } from './reasoning-format.js';

const columns: DataTableColumn<ReasoningEvaluationCheck>[] = [
  {
    key: 'label',
    header: 'Check',
    render: (check) => (
      <div className="min-w-0">
        <div className="font-medium text-heading">{check.label}</div>
        <div className="text-xs text-muted">{check.reason}</div>
      </div>
    ),
  },
  { key: 'value', header: 'Value', render: (check) => `${checkValue(check)} (n ${check.n})` },
  { key: 'rule', header: 'Threshold', render: (check) => check.rule },
  {
    key: 'status',
    header: 'Verdict',
    render: (check) => <Badge variant={CHECK_STATUS[check.status].variant}>{CHECK_STATUS[check.status].label}</Badge>,
  },
];

export const ReasoningRunSheet: React.FC<{ run: ReasoningEvaluation | null; onClose: () => void }> = ({
  run,
  onClose,
}) => (
  <Sheet
    open={run !== null}
    onClose={onClose}
    width="xl"
    title={run?.title ?? 'Reasoning evaluation'}
    description={
      run
        ? `Asked on: ${askedOn(run.surface, run.policyId)} · held out after ${run.split?.slice(0, 10) ?? '—'} · persona version ${run.versionId ?? '—'}`
        : undefined
    }
  >
    {run && (
      <div className="space-y-4">
        {run.report ? (
          <p className="text-sm text-muted">
            Asked {run.report.asked}, answered {run.report.answered}, failed {run.report.failed}. Outcomes resolved:{' '}
            {run.report.outcome.n}.
          </p>
        ) : (
          <p className="text-sm text-muted">This run's report could not be read (an older or damaged record).</p>
        )}
        <DataTable framed aria-label="Checks of this run" columns={columns} rows={run.checks} rowKey={(c) => c.key} />
      </div>
    )}
  </Sheet>
);
